import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { CorePlanImage, PlanImage } from '../../types/planImages.interface.d'
import {
  getCommunityImages,
  GetCommunityPlanImagesParams,
} from '../actions/get-community-images'

export interface CommunityPlanImagesMap {
  [key: string]: PlanImage[]
}

export interface CoreRenderingsMap {
  [plan: string]: {
    [elevation: string]: {
      [region: string]: CorePlanImage
    }
  }
}

export interface CoreFloorPlansMap {
  [plan: string]: {
    [region: string]: CorePlanImage[]
  }
}

export interface ViewOnlyImagesState {
  allElevationsLoaded: Record<string, boolean>
  communityImages: CommunityPlanImagesMap
  coreFloorPlans: CoreFloorPlansMap
  coreRenderings: CoreRenderingsMap
  error: string | null
  loading: boolean
}

export const initialState: ViewOnlyImagesState = {
  allElevationsLoaded: {},
  communityImages: {},
  coreFloorPlans: {},
  coreRenderings: {},
  error: null,
  loading: false,
}

export const fetchCommunityPlanImages = createAsyncThunk(
  'communityPlanImages',
  async (params: GetCommunityPlanImagesParams) => {
    const { communityNumber, planNumber } = params

    const imagesPlanKey = `${communityNumber}_${planNumber}`

    return (
      (await getCommunityImages({ communityNumber, planNumber }))?.data as {
        images: Record<string, PlanImage[]>
      }
    )?.images[imagesPlanKey]
  }
)

export const viewOnlyImageSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(fetchCommunityPlanImages.pending, (state) => {
      state.error = null
      state.loading = true
    })
    builder.addCase(fetchCommunityPlanImages.fulfilled, (state, action) => {
      const { communityNumber, planNumber } = action.meta.arg
      const imagePlanKey = `${communityNumber}_${planNumber}`
      state.communityImages[imagePlanKey] = action.payload
      state.error = null
      state.loading = false
    })
    builder.addCase(fetchCommunityPlanImages.rejected, (state, action) => {
      state.error = action.error.message ?? 'Error fetching plan image(s)'
      state.loading = false
    })
  },
  initialState,
  name: 'viewOnlyImageSlice',
  reducers: {
    setAllElevationsLoaded: (state, action: { payload: string }) => {
      state.allElevationsLoaded[action.payload] = true
    },
    updateCoreFloorPlans: (state, action: { payload: CoreFloorPlansMap }) => {
      Object.entries(action.payload).forEach(([planName, regionMappings]) => {
        if (!state.coreFloorPlans[planName]) {
          state.coreFloorPlans[planName] = {}
        }

        Object.entries(regionMappings).forEach(([region, images]) => {
          state.coreFloorPlans[planName][region] = images
        })
      })
    },
    updateCorePlanRenderings: (
      state,
      action: { payload: CoreRenderingsMap }
    ) => {
      Object.entries(action.payload).forEach(
        ([planName, elevationsMapping]) => {
          if (!state.coreRenderings[planName]) {
            state.coreRenderings[planName] = {}
          }

          Object.entries(elevationsMapping).forEach(
            ([elevation, regionMappings]) => {
              if (!state.coreRenderings[planName][elevation]) {
                state.coreRenderings[planName][elevation] = {}
              }

              Object.entries(regionMappings).forEach(([region, images]) => {
                state.coreRenderings[planName][elevation][region] = images
              })
            }
          )
        }
      )
    },
  },
})

export const {
  setAllElevationsLoaded,
  updateCoreFloorPlans,
  updateCorePlanRenderings,
} = viewOnlyImageSlice.actions
